<!--
  KAN-32: schedule the actual report FILE (not the Dashboard Digest's KPI
  text/PDF) to go out by email/WhatsApp, per report type, on this campaign.
  Lives in the Reports tab next to CampaignReportsPanel's manual-download
  cards -- this is a report-delivery feature, not a general campaign
  setting, so it sits with the other report surface rather than the
  Settings tab where DashboardDigestSettings lives.

  One Save button covers all 4 report types' config in one call (they're
  saved as one JSON object server-side, report_digest_config) -- same
  single-object-save UX DashboardDigestSettings.vue already established.
  Each report type is its own collapsible card, independently
  enabled/scheduled/channeled/recipiented; "Send now" fires an immediate
  send of whatever is CURRENTLY SAVED for that one report type, so it's
  disabled until this component's own save has succeeded at least once
  with that report type actually configured.
-->
<template>
  <div class="rds-root">
    <header class="rds-head">
      <div>
        <p class="crx-note" style="margin: 0">
          Automatically email or WhatsApp any of these reports on a schedule, instead of coming
          back here to download them manually. Each report type has its own recipients,
          channel, and schedule.
        </p>
      </div>
      <button type="button" class="tw-btn tw-btn--primary" :disabled="saving" @click="save">
        <v-icon icon="mdi-content-save-outline" size="16" />
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </header>

    <article v-for="def in REPORT_DEFS" :key="def.key" class="rds-card">
      <button type="button" class="rds-card__head" @click="toggleExpanded(def.key)">
        <v-icon :icon="def.icon" size="20" />
        <span class="rds-card__title">{{ def.title }}</span>
        <span class="rds-card__summary">{{ summaryFor(def.key) }}</span>
        <v-icon :icon="expanded[def.key] ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="20" />
      </button>

      <v-expand-transition>
        <div v-if="expanded[def.key]" class="rds-card__body">
          <!-- Email -->
          <div class="rds-channel">
            <div class="rds-channel__head">
              <span class="rds-channel__label"><v-icon icon="mdi-email-outline" size="16" /> Email</span>
              <v-switch
                :model-value="cfg(def.key).send_by_email"
                color="primary" density="compact" hide-details
                :disabled="saving"
                @update:model-value="(v) => patchChannel(def.key, { send_by_email: !!v, ...(v ? {} : { email_recipients: [] }) })"
              />
            </div>
            <RecipientChipInput
              v-if="cfg(def.key).send_by_email"
              :model-value="cfg(def.key).email_recipients"
              kind="email"
              :disabled="saving"
              @update:model-value="(v) => patchChannel(def.key, { email_recipients: v })"
            />
            <p v-if="cfg(def.key).send_by_email && !cfg(def.key).email_recipients.length" class="rds-warning">
              Email is on but there's nobody to send it to yet -- add a recipient above.
            </p>
          </div>

          <!-- WhatsApp -->
          <div class="rds-channel">
            <div class="rds-channel__head">
              <span class="rds-channel__label"><v-icon icon="mdi-whatsapp" size="16" /> WhatsApp</span>
              <v-switch
                :model-value="cfg(def.key).send_by_whatsapp"
                color="primary" density="compact" hide-details
                :disabled="saving"
                @update:model-value="(v) => patchChannel(def.key, { send_by_whatsapp: !!v, ...(v ? {} : { whatsapp_recipients: [] }) })"
              />
            </div>
            <RecipientChipInput
              v-if="cfg(def.key).send_by_whatsapp"
              :model-value="cfg(def.key).whatsapp_recipients"
              kind="whatsapp"
              :disabled="saving"
              @update:model-value="(v) => patchChannel(def.key, { whatsapp_recipients: v })"
            />
            <p v-if="cfg(def.key).send_by_whatsapp && !cfg(def.key).whatsapp_recipients.length" class="rds-warning">
              WhatsApp is on but there's nobody to send it to yet -- add a number above.
            </p>
          </div>

          <!-- Schedule -->
          <div v-if="cfg(def.key).send_by_email || cfg(def.key).send_by_whatsapp" class="rds-schedule">
            <div class="rds-cadence" style="border-top: none; padding-top: 0; margin-top: 0">
              <div class="rds-cadence__head">
                <span class="rds-label" style="margin: 0">Daily</span>
              </div>
              <v-select
                :model-value="cfg(def.key).times"
                :items="hourOptions"
                placeholder="Pick one or more times"
                variant="outlined" density="compact" hide-details multiple chips closable-chips
                :disabled="saving"
                class="mt-2"
                @update:model-value="(v) => patchChannel(def.key, { times: v })"
              />
              <p class="rds-hint">Picking a time here IS turning daily on -- clear all times to turn it off. Fires every day at each time picked (pick more than one for multiple sends a day).</p>
              <p v-if="!cfg(def.key).times.length && !weeklyOrMonthlyOn(def.key)" class="rds-warning">
                Pick at least one send time above (or turn on weekly/monthly below), or this won't go out.
              </p>
            </div>

            <div class="rds-cadence">
              <div class="rds-cadence__head">
                <span class="rds-label" style="margin: 0">Weekly</span>
                <v-switch
                  :model-value="cfg(def.key).weekly_enabled"
                  color="primary" density="compact" hide-details
                  :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { weekly_enabled: !!v, ...(v ? {} : { weekly_day: '', weekly_time: '' }) })"
                />
              </div>
              <div v-if="cfg(def.key).weekly_enabled" class="rds-cadence__fields">
                <v-select
                  :model-value="cfg(def.key).weekly_day" :items="weekdayOptions" item-title="label" item-value="value"
                  placeholder="Day" variant="outlined" density="compact" hide-details :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { weekly_day: v })"
                />
                <v-select
                  :model-value="cfg(def.key).weekly_time" :items="hourOptions"
                  placeholder="Time" variant="outlined" density="compact" hide-details :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { weekly_time: v })"
                />
              </div>
            </div>

            <div class="rds-cadence">
              <div class="rds-cadence__head">
                <span class="rds-label" style="margin: 0">Monthly</span>
                <v-switch
                  :model-value="cfg(def.key).monthly_enabled"
                  color="primary" density="compact" hide-details
                  :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { monthly_enabled: !!v, ...(v ? {} : { monthly_day: null, monthly_time: '' }) })"
                />
              </div>
              <div v-if="cfg(def.key).monthly_enabled" class="rds-cadence__fields">
                <v-select
                  :model-value="cfg(def.key).monthly_day" :items="monthDayOptions"
                  placeholder="Day of month" variant="outlined" density="compact" hide-details :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { monthly_day: v })"
                />
                <v-select
                  :model-value="cfg(def.key).monthly_time" :items="hourOptions"
                  placeholder="Time" variant="outlined" density="compact" hide-details :disabled="saving"
                  @update:model-value="(v) => patchChannel(def.key, { monthly_time: v })"
                />
              </div>
            </div>

            <button
              type="button" class="rds-send-now"
              :disabled="sendingNow[def.key] || !canSendNow(def.key)"
              @click="sendNow(def.key)"
            >
              <v-icon v-if="sendingNow[def.key]" icon="mdi-loading" class="crx-spin" size="16" />
              <v-icon v-else icon="mdi-send-outline" size="16" />
              {{ sendingNow[def.key] ? 'Sending…' : 'Send now' }}
            </button>
            <p v-if="!canSendNow(def.key)" class="rds-hint">
              Save with a channel + recipients first -- Send now always sends whatever is currently
              saved, not unsaved edits above.
            </p>
          </div>
        </div>
      </v-expand-transition>
    </article>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import RecipientChipInput from '@/components/Shared/RecipientChipInput.vue'
import { updateTaskReportDigestConfig, triggerReportDigestNow } from '@/api/campaignsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'ReportDigestSettings' })

const props = defineProps({
  campaignId: { type: String, default: '' },
  // The whole report_digest_config object, keyed by report type -- read
  // once on mount/change from the parent's own normalized campaign record
  // (campaigns.js's reportDigestConfig), same "load once, own save" pattern
  // DashboardDigestSettings.vue's host page already uses for the dashboard
  // digest.
  modelValue: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['saved', 'error'])

const REPORT_DEFS = [
  { key: 'callLog', title: 'Call log', icon: 'mdi-phone-log-outline' },
  { key: 'statusHistory', title: 'Status change history', icon: 'mdi-timeline-clock-outline' },
  { key: 'agentActivity', title: 'Agent activity log', icon: 'mdi-account-clock-outline' },
  { key: 'leadDetail', title: 'Full lead detail dump', icon: 'mdi-table-large' },
]

function emptyChannelConfig() {
  return {
    send_by_email: false, email_recipients: [],
    send_by_whatsapp: false, whatsapp_recipients: [],
    times: [],
    weekly_enabled: false, weekly_day: '', weekly_time: '',
    monthly_enabled: false, monthly_day: null, monthly_time: '',
  }
}

function hydrate(source) {
  const result = {}
  for (const def of REPORT_DEFS) {
    const existing = source?.[def.key] || {}
    result[def.key] = { ...emptyChannelConfig(), ...existing }
  }
  return result
}

const form = reactive(hydrate(props.modelValue))
// Separate from `form` (the live, possibly-unsaved edits) -- this mirrors
// whatever is ACTUALLY persisted server-side right now, so "Send now" can
// be enabled correctly on a fresh page load for a report type that was
// already configured in a PREVIOUS visit, not just within this one. Fixed
// live 2026-08-19: the first version gated Send Now on a session-local
// "did I click Save just now" flag, which meant reloading the page (or
// simply never having touched Save this visit) always showed Send Now as
// unavailable even when the config was genuinely saved and working.
const persisted = reactive(hydrate(props.modelValue))
// campaign.value on the host page can populate AFTER this component has
// already mounted (its own campaign fetch resolves asynchronously, same
// "set from two independent places" situation CampaignDetailPage.vue's own
// Dashboard Digest wiring documents) -- without this, a v-if="isAdmin"
// mount that lands before that fetch resolves would hydrate() once against
// an empty {} and never see the real saved config. Same accepted tradeoff
// as that existing digest wiring: re-hydrates on ANY prop change, not just
// post-save, so mid-edit unsaved changes could in principle be clobbered by
// an unrelated reload -- rare in practice, and this component's Save is a
// single explicit action, not autosave, so the blast radius is small.
watch(() => props.modelValue, (next) => {
  Object.assign(form, hydrate(next))
  Object.assign(persisted, hydrate(next))
})

const expanded = reactive(Object.fromEntries(REPORT_DEFS.map((d) => [d.key, false])))
const saving = ref(false)
const sendingNow = reactive(Object.fromEntries(REPORT_DEFS.map((d) => [d.key, false])))

function canSendNow(key) {
  const c = persisted[key]
  return (c.send_by_email && c.email_recipients.length > 0) || (c.send_by_whatsapp && c.whatsapp_recipients.length > 0)
}

function cfg(key) {
  return form[key]
}

function patchChannel(key, partial) {
  form[key] = { ...form[key], ...partial }
}

function toggleExpanded(key) {
  expanded[key] = !expanded[key]
}

function weeklyOrMonthlyOn(key) {
  return form[key].weekly_enabled || form[key].monthly_enabled
}

function summaryFor(key) {
  const c = form[key]
  const parts = []
  if (c.send_by_email) parts.push(`Email (${c.email_recipients.length})`)
  if (c.send_by_whatsapp) parts.push(`WhatsApp (${c.whatsapp_recipients.length})`)
  return parts.length ? parts.join(' · ') : 'Off'
}

const hourOptions = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)
const weekdayOptions = [
  { label: 'Monday', value: 'MON' }, { label: 'Tuesday', value: 'TUE' }, { label: 'Wednesday', value: 'WED' },
  { label: 'Thursday', value: 'THU' }, { label: 'Friday', value: 'FRI' }, { label: 'Saturday', value: 'SAT' }, { label: 'Sunday', value: 'SUN' },
]
const monthDayOptions = Array.from({ length: 31 }, (_, i) => i + 1)

async function save() {
  if (!props.campaignId) return
  saving.value = true
  try {
    const response = await updateTaskReportDigestConfig({
      task_id: props.campaignId,
      report_digest_config: { ...form },
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Could not save report delivery settings.'))
      return
    }
    // Snapshot exactly what was just saved -- the parent page doesn't
    // reload the campaign after this save (unlike Dashboard Digest's own
    // host wiring, which does), so relying on props.modelValue alone would
    // leave Send Now looking unavailable immediately after a real,
    // successful save.
    Object.assign(persisted, hydrate(form))
    emit('saved', 'Report delivery settings saved.')
  } catch (err) {
    emit('error', getApiMessage(err, 'Could not save report delivery settings.'))
  } finally {
    saving.value = false
  }
}

async function sendNow(reportType) {
  if (!props.campaignId) return
  sendingNow[reportType] = true
  try {
    const response = await triggerReportDigestNow({ task_id: props.campaignId, report_type: reportType })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Could not send that report right now.'))
      return
    }
    emit('saved', getApiMessage(response, 'Sent.'))
  } catch (err) {
    emit('error', getApiMessage(err, 'Could not send that report right now.'))
  } finally {
    sendingNow[reportType] = false
  }
}

defineExpose({ save })
</script>

<style scoped>
.rds-root {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.rds-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.rds-card {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 0.85rem;
  overflow: hidden;
}

.rds-card__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
}

.rds-card__title {
  font-weight: 650;
  font-size: 0.9rem;
}

.rds-card__summary {
  margin-left: auto;
  font-size: 0.76rem;
  opacity: 0.6;
}

.rds-card__body {
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 0.85rem;
}

.rds-channel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rds-channel__label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.rds-label {
  font-size: 0.72rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.6;
  margin: 0 0 0.35rem;
}

.rds-warning {
  font-size: 0.76rem;
  color: #b45309;
  margin: 0.35rem 0 0;
}

.rds-hint {
  font-size: 0.76rem;
  opacity: 0.6;
  margin: 0.35rem 0 0;
}

.rds-schedule {
  border-top: 1px dashed rgba(148, 163, 184, 0.25);
  padding-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.rds-cadence {
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 0.6rem;
  margin-top: 0.1rem;
}

.rds-cadence__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.rds-cadence__fields {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.rds-cadence__fields > * {
  flex: 1;
  min-width: 0;
}

.rds-send-now {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  padding: 0.4rem 0.85rem;
  border-radius: 0.6rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.rds-send-now:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
